import { DateTime } from 'luxon'

export * from './type-predicate'

/**
 * Create a promise which resolves after provided timeout in ms
 *
 * Note that given the js event loop you will never be able to guarantee
 * this timeout too precisely but it should at least be safe to assume
 * "at least this time in ms will have passed"
 */
export function wait(timeout: number) {
  return new Promise<void>((resolve) => {
    const wait = setTimeout(() => {
      clearTimeout(wait)
      resolve()
    }, timeout)
  })
}

/**
 * Is a value like a promise with an async `then` handler?
 *
 * Ie. Can be awaited in an async function
 */
export const isThenable = (value: any): value is Promise<any> =>
  !!value && typeof value['then'] === 'function'

/**
 * Is a value considered "nullish" in js?
 *
 * Ie. explicitly is `null` or `undefined`
 */
export const isNullish = (value: any): value is null | undefined =>
  value === null || typeof value === 'undefined'

/**
 * Get age in number of years from valid iso date string
 */
export const getCurrentAge = (date: string) =>
  Math.floor(DateTime.now().diff(DateTime.fromISO(date), 'years').years)

/**
 * Add a set number of weekdays to a date in string format
 */
export const addWeekdays = ({
  dateString,
  numToAdd = 2,
}: {
  dateString: string
  numToAdd?: number
}) => {
  const date = new Date(dateString)
  for (let i = 1; i <= numToAdd; i++) {
    date.setDate(date.getDate() + 1)
    if (date.getDay() === 6) {
      // saturday
      date.setDate(date.getDate() + 2)
    } else if (date.getDay() === 0) {
      // sunday
      date.setDate(date.getDate() + 1)
    }
  }
  return date
}

/**
 * A simple version of a cartesian product function which sacrifices the
 * ability to pass in N arrays in favour of stricter type safety on N=2
 * arrays, producing a simple array of 2-Tuple
 *
 * @see https://gist.github.com/ssippe/1f92625532eef28be6974f898efb23ef
 */
export const simpleCartesian = <T>(a: T[], b: T[]): (readonly [T, T])[] => {
  return a.flatMap((a1) => b.map((b1) => [a1, b1] as const))
}
